/**
 * <LivretValidationsRecap />
 *
 * Encart récapitulatif des validations REMC posées par l'enseignant pendant
 * la séance. Affiché à l'écran ET imprimé sur le PDF (no-print non appliqué).
 * Sert de support pour que l'élève reporte ces statuts dans son livret personnel.
 *
 * Props :
 *  - validations : object { 'C1-e': 'acquis' | 'en_cours' | 'non_acquis' }
 *  - accent : couleur de bordure (ex '#1d4ed8' pour Plateau, '#7c3aed' Circulation)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Circle, Clock, CheckCircle2, ArrowRight, Share2 } from 'lucide-react';
import { Button } from '../ui/button';
import { getLivretItem } from '../../data/teacher/evalInference';
import ShareValidationsDialog from './ShareValidationsDialog';

const CATEGORIE_COLORS = {
  C1: '#0891b2',
  C2: '#7c3aed',
  C3: '#dc2626',
  C4: '#16a34a',
};

const STATUS_META = {
  non_acquis: { label: 'À travailler', color: '#64748b', Icon: Circle },
  en_cours:   { label: 'En cours',     color: '#d97706', Icon: Clock },
  acquis:     { label: 'Acquis',       color: '#16a34a', Icon: CheckCircle2 },
};

export default function LivretValidationsRecap({ validations, accent = '#1d4ed8', studentName, context }) {
  const [shareOpen, setShareOpen] = useState(false);
  const entries = Object.entries(validations || {});
  if (entries.length === 0) return null;

  // Tri par catégorie REMC puis code
  entries.sort(([a], [b]) => a.localeCompare(b));

  return (
    <section
      className="rounded-2xl bg-card border-2 p-5 sm:p-6"
      style={{ borderColor: `${accent}80` }}
      data-testid="livret-validations-recap"
    >
      <div className="flex items-start gap-3 flex-wrap mb-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0"
          style={{ background: accent }}
          aria-hidden="true"
        >
          <BookOpen className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-black tracking-tight">
            Validations REMC de la séance
          </h3>
          <p className="text-[11px] text-muted-foreground italic leading-snug mt-0.5">
            Statuts à reporter par l&apos;élève dans son livret d&apos;apprentissage personnel
            (
            <Link
              to="/mon-livret-moto"
              className="font-bold underline hover:no-underline no-print"
              data-testid="link-mon-livret-moto"
            >
              code2roues.fr · Mon livret moto
            </Link>
            ).
          </p>
        </div>
        <span
          className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full text-white"
          style={{ background: accent }}
        >
          {entries.length} item{entries.length > 1 ? 's' : ''}
        </span>
      </div>

      <ul className="space-y-1.5" data-testid="livret-validations-list">
        {entries.map(([code, status]) => {
          const item = getLivretItem(code);
          if (!item) return null;
          const catColor = CATEGORIE_COLORS[item.categorie] || accent;
          const meta = STATUS_META[status] || STATUS_META.non_acquis;
          const Icon = meta.Icon;
          return (
            <li
              key={code}
              className="flex items-center gap-2 rounded-lg border border-border bg-background/40 px-2.5 py-2"
              data-testid={`livret-recap-${code}`}
            >
              <span
                className="inline-flex items-center justify-center rounded-md px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white flex-shrink-0 min-w-[42px]"
                style={{ background: catColor }}
              >
                {code}
              </span>
              <span className="text-xs sm:text-sm font-semibold leading-snug flex-1 min-w-0">
                {item.intitule}
              </span>
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider text-white flex-shrink-0"
                style={{ background: meta.color }}
              >
                <Icon className="w-3 h-3" />
                {meta.label}
              </span>
            </li>
          );
        })}
      </ul>

      {/* CTAs visibles uniquement à l'écran */}
      <div className="no-print mt-3 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => setShareOpen(true)}
          className="gap-1.5 text-xs text-white"
          style={{ background: accent }}
          data-testid="share-validations-btn"
        >
          <Share2 className="w-3.5 h-3.5" />
          Partager au livret élève
        </Button>
        <Link
          to="/mon-livret-moto"
          className="inline-flex items-center gap-1.5 text-xs font-bold underline hover:no-underline"
          style={{ color: accent }}
          data-testid="link-go-livret-eleve"
        >
          Ouvrir mon livret <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <ShareValidationsDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        validations={validations}
        studentName={studentName}
        context={context}
      />
    </section>
  );
}
